import { format, startOfMonth, subMonths } from 'date-fns';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  type RevenueTrend,
  useGetRevenueTrend,
} from '~entities/dashboard/api/get-revenue-trend';

import { formatCurrencyAmount } from '~features/new-dashboard/helpers/format-currency';
import { RevenueTrendCSVDownload } from '~features/new-dashboard/ui/revenue-trend-csv-download';

import { useGetCurrency } from '~shared/api/currency-api';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

interface RevenueTrendChartProps {
  sessionId: string;
}

const today = new Date();
const formattedToday = format(today, 'yyyy-MM-dd');
const sixMonthsAgo = subMonths(today, 5);
const firstOfMonth = startOfMonth(sixMonthsAgo);
const formattedFirstOfMonth = format(firstOfMonth, 'yyyy-MM-dd');

export function RevenueTrendChart({ sessionId }: RevenueTrendChartProps) {
  const [csvDownloadDialogOpen, setCsvDownloadDialogOpen] = useState(false);
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  const { data: revenueTrendData, isLoading: isRevenueTrendLoading } =
    useGetRevenueTrend({
      sessionId,
      startDate: formattedFirstOfMonth,
      endDate: formattedToday,
    });

  const formatCurrency = (value: number) =>
    `${currencySymbol}${formatCurrencyAmount(value)}`;

  return (
    <>
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
            <div>
              <CardTitle className='text-lg font-semibold text-[#000814]'>
                Revenue Trend
              </CardTitle>
              <CardDescription>Last 6 months revenue trend.</CardDescription>
            </div>
            <Button
              onClick={() => {
                setCsvDownloadDialogOpen(true);
              }}
              variant='outline'
              size='sm'
              className='group flex h-fit items-center gap-1 border-none p-0 text-xs text-[#6B7280] shadow-none transition-colors hover:bg-transparent hover:text-black'
            >
              View Full Report
              <ArrowRight className='size-3 transition-transform group-hover:translate-x-0.5' />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isRevenueTrendLoading ? (
            <div className='flex h-[280px] items-center justify-center'>
              <Loader2 className='size-10 animate-spin' />
            </div>
          ) : (
            <ResponsiveContainer width='100%' height={280}>
              <AreaChart data={revenueTrendData as Array<RevenueTrend>}>
                <defs>
                  <linearGradient id='colorCurrent' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#FFC300' stopOpacity={0.3} />
                    <stop offset='95%' stopColor='#FFC300' stopOpacity={0} />
                  </linearGradient>
                  {/* {showComparison && (
                  <>
                    <linearGradient
                      id='colorLastYear'
                      x1='0'
                      y1='0'
                      x2='0'
                      y2='1'
                    >
                      <stop offset='5%' stopColor='#94A3B8' stopOpacity={0.2} />
                      <stop offset='95%' stopColor='#94A3B8' stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id='colorTwoYears'
                      x1='0'
                      y1='0'
                      x2='0'
                      y2='1'
                    >
                      <stop
                        offset='5%'
                        stopColor='#CBD5E1'
                        stopOpacity={0.15}
                      />
                      <stop offset='95%' stopColor='#CBD5E1' stopOpacity={0} />
                    </linearGradient>
                  </>
                )} */}
                </defs>
                <CartesianGrid
                  strokeDasharray='3 3'
                  stroke='#E5E7EB'
                  vertical={false}
                />
                <XAxis
                  dataKey='month'
                  stroke='#475569'
                  fontSize={12}
                  fontWeight={500}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke='#475569'
                  fontSize={12}
                  fontWeight={500}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value: number) => {
                    if (value < 1000) {
                      return `${currencySymbol}${value}`;
                    }
                    return `${currencySymbol}${value / 1000}k`;
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    padding: '8px 12px',
                  }}
                  formatter={(value: number, _name: string) => {
                    // const label =
                    //   name === 'current'
                    //     ? 'Current Year'
                    //     : name === 'lastYear'
                    //       ? 'Last Year'
                    //       : 'Two Years Ago';
                    return [formatCurrency(value), 'Amount'];
                  }}
                />
                {/* {showComparison && (
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType='line'
                  formatter={(value) => {
                    const labels: Record<string, string> = {
                      current: 'Current Year',
                      lastYear: 'Last Year',
                      twoYearsAgo: 'Two Years Ago',
                    };
                    return (
                      <span className='text-sm font-medium text-[#6B7280]'>
                        {labels[value as keyof typeof labels]}
                      </span>
                    );
                  }}
                />
              )} */}
                <Area
                  type='monotone'
                  dataKey='amount'
                  stroke='#FFC300'
                  strokeWidth={2}
                  fill='url(#colorCurrent)'
                  name='amount'
                  animationDuration={240}
                />
                {/* {showComparison && (
                <>
                  <Area
                    type='monotone'
                    dataKey='lastYear'
                    stroke='#94A3B8'
                    strokeWidth={2}
                    fill='url(#colorLastYear)'
                    strokeDasharray='5 5'
                    name='lastYear'
                    animationDuration={240}
                  />
                  <Area
                    type='monotone'
                    dataKey='twoYearsAgo'
                    stroke='#CBD5E1'
                    strokeWidth={1.5}
                    fill='url(#colorTwoYears)'
                    strokeDasharray='3 3'
                    name='twoYearsAgo'
                    animationDuration={240}
                  />
                </>
              )} */}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
      {csvDownloadDialogOpen && (
        <RevenueTrendCSVDownload
          open={csvDownloadDialogOpen}
          onOpenChange={setCsvDownloadDialogOpen}
          sessionId={sessionId}
          startDate={formattedFirstOfMonth}
          endDate={formattedToday}
        />
      )}
    </>
  );
}
